import { useContext, useMemo, useState, type ReactNode } from 'react'
import { Select } from 'radix-ui'
import LockIcon from '@/CustomIcons/LockIcon'
import SelectedCheckmarkIcon from '@/CustomIcons/SelectedCheckmarkIcon'
import LeftChevronIcon from '@/CustomIcons/LeftChevron'
import CaretDownIcon from '@/CustomIcons/CaretDownIcon'
import CaretUpIcon from '@/CustomIcons/CaretUpIcon'
import { cn } from '@/utlis/cn'
import Tooltip from '@/UIComponents2/Tooltip'
import { PortalContext } from '@/UIComponents2/PortalContext'
import './simple-select.css'

export type Item = {
    value: string
    label: string
    description?: string
    disabled?: boolean
    locked?: boolean
    tooltip?: string
}

export type Group = {
    label?: string
    items: readonly Item[]
}

export interface SimpleSelectProps {
    items: readonly (Item | Group | ReactNode)[]
    value?: string
    defaultValue?: string
    onChange?: (value: string) => void
    onClick?: (value: string) => void
    placeholder?: string
    leftElement?: ReactNode
    className?: string
    triggerClassName?: string
    contentClassName?: string
    disabled?: boolean
    name?: string
    widthMatching?: boolean
    forcePlacement?: 'bottom' | 'top'
    label?: ReactNode
    onOpenChange?: (open: boolean) => void
    deselectable?: boolean
    open?: boolean
}

export function SimpleSelect(props: SimpleSelectProps) {
    const baseLayoutStyles = 'flex w-full py-[8px] gap-2 pl-4 pr-2 items-center justify-between'
    const baseBorderStyles = 'rounded-[12px] border'

    const [isOpen, setIsOpen] = useState(props.open ?? false)

    const handleChange = (val: string) => {
        props.onChange?.(val)
    }

    const portalContainer = useContext(PortalContext)

    const renderItem = (item: Item, indent?: boolean) => {
        return (
            <Tooltip text={item.tooltip} key={item.value}>
                <Select.Item
                    key={item.value}
                    onSelect={() => {
                        props.onClick?.(item.value)
                    }}
                    value={item.value}
                    textValue={item.label}
                    disabled={item.disabled || item.locked}
                    className={cn(
                        'flex justify-between items-center cursor-pointer hover:bg-white/10 pr-[16px] pl-[30px] py-[6px]',
                        'focus-visible:outline-none focus-visible:bg-white/10',
                        item.disabled && 'opacity-50 cursor-not-allowed',
                        item.locked && 'opacity-50 cursor-not-allowed font-light',
                        item.value === props.value && 'font-medium'
                    )}
                >
                    <Select.ItemText>
                        {item.description ? `${item.label} - ${item.description}` : item.label}
                    </Select.ItemText>
                    {item.locked && <LockIcon />}
                    <Select.ItemIndicator className="text-muse-yellow">
                        <SelectedCheckmarkIcon />
                    </Select.ItemIndicator>
                </Select.Item>
            </Tooltip>
        )
    }

    const content = useMemo(() => {
        return props.items.map((item) => {
            if (itemIsItem(item)) {
                return renderItem(item)
            } else if (itemIsGroup(item)) {
                return (
                    <Select.Group key={item.label} className="flex flex-col">
                        <Select.Label className="opacity-65 px-[16px] py-[6px]">{item.label}</Select.Label>
                        {item.items.map((item) => renderItem(item, true))}
                    </Select.Group>
                )
            } else {
                return item
            }
        })
    }, [props.items])

    return (
        <Select.Root
            onOpenChange={(open) => {
                props.onOpenChange?.(open)
                setIsOpen(open)
            }}
            value={props.value}
            open={props.open}
            defaultValue={props.defaultValue}
            onValueChange={handleChange}
            disabled={props.disabled}
        >
            <div className={cn('min-w-0 flex flex-col gap-2', props.className)}>
                {!!props.label && <div>{props.label}</div>}
                <Select.Trigger
                    className={cn(
                        'select-trigger',
                        baseLayoutStyles,
                        baseBorderStyles,
                        'border-muse-toplit',
                        'shadow-glow',
                        'bg-[rgba(255,255,255,0.03)] cursor-pointer',
                        props.disabled && 'opacity-50 cursor-not-allowed',
                        props.triggerClassName
                    )}
                >
                    <div className="flex items-center gap-4 min-w-0">
                        {props.leftElement}
                        <div className="select-selected-label truncate whitespace-nowrap">
                            <Select.Value placeholder={props.placeholder} className="truncate pt-[2px] min-w-0" />
                        </div>
                    </div>
                    <Select.Icon
                        className={cn('transition-transform duration-100', isOpen ? 'rotate-90' : '-rotate-90')}
                    >
                        <LeftChevronIcon className="opacity-65" style={{ fontSize: '1.1rem' }} />
                    </Select.Icon>
                </Select.Trigger>
            </div>
            <Select.Portal container={portalContainer}>
                <Select.Content
                    position="popper"
                    side={props.forcePlacement}
                    sideOffset={5}
                    className={cn(
                        'select-content',
                        baseBorderStyles,
                        'z-39 border-white/15',
                        'shadow-[inset_2px_4px_16px_0px_rgba(255,255,255,0.1)]',
                        'bg-muse-menu py-[0px] bg-blend-luminosity',
                        props.contentClassName,
                        props.widthMatching && 'popper'
                    )}
                >
                    <div className="h-[20px] w-full">
                        <Select.ScrollUpButton className="flex items-center justify-center w-full h-[20px]">
                            <CaretUpIcon />
                        </Select.ScrollUpButton>
                    </div>
                    <Select.Viewport className="max-h-[40vh] overflow-y-auto">{content}</Select.Viewport>
                    <div className="h-[20px] w-full">
                        <Select.ScrollDownButton className="flex items-center justify-center w-full h-[20px]">
                            <CaretDownIcon />
                        </Select.ScrollDownButton>
                    </div>
                </Select.Content>
            </Select.Portal>
        </Select.Root>
    )
}

function itemIsItem(item: Item | Group | ReactNode): item is Item {
    return (item as Item).value !== undefined
}

function itemIsGroup(item: Item | Group | ReactNode): item is Group {
    return (item as Group).items !== undefined
}
